/**
 * W-31 §10.3 — how a value leaves this app in a spreadsheet, and the one rule
 * every generated file obeys: **a cell can never become a formula**.
 *
 * "Hatalı satırları indir" writes the owner's own cells back, so whatever a
 * supplier typed into their sheet ends up in a file that Excel opens. A cell
 * that starts with `=`, `+`, `-`, `@`, a tab or a CR is neutralised with a
 * leading apostrophe (§10.3), in CSV and in XLSX alike — an XLSX string cell is
 * not evaluated today, but the same file is copied, re-saved and pasted, and the
 * guard costs one character.
 *
 * Numbers go out with a comma decimal and no thousands separator: that is what
 * `private.import_num` reads back unambiguously (a comma makes it strip dots and
 * swap, no comma makes it read the dot as the decimal point) and what
 * `normalizeNumberText` reads with the Turkish default, so a file this app
 * writes survives its own re-upload.
 */
import { D } from '../money';

/** Cells Excel would evaluate; `\t` and `\r` are here because Excel splits on them. */
const FORMULA_START = /^[=+\-@\t\r]/;

export function isFormulaLike(value: string): boolean {
  return FORMULA_START.test(value);
}

/**
 * Idempotent: a value that was already neutralised starts with `'` and is left
 * alone, so a plan that guards its cells can still be written through `toCsv`.
 */
export function neutralizeFormula(value: string): string {
  return isFormulaLike(value) ? `'${value}` : value;
}

/**
 * Decimals kept from a numeric cell. Every column that reads one is narrower
 * than this (quantities 3, money 4, rates 8), so nothing real is lost — while a
 * cached `=0,1+0,2` arrives as 0.30000000000000004 and has to come out as the
 * "0,3" Excel itself shows.
 */
const CELL_SCALE = 10;

/**
 * A native numeric cell as text: "412,5", "1561,6", "-46". Decimal.js does the
 * conversion so a large figure never turns into "1e+21" and a float artefact
 * never turns into a value the server rejects for its decimals.
 */
export function numberCellText(value: number): string {
  if (!Number.isFinite(value)) return '';
  return D(value).toDecimalPlaces(CELL_SCALE).toFixed().replace('.', ',');
}

/**
 * A native date cell as an ISO date (§8.3). `private.import_date` casts it
 * directly and `normalizeDateText` passes it through untouched, so both date
 * formats of step 2 read it the same way. Excel dates carry no zone — the UTC
 * parts are the ones the cell shows.
 */
export function dateCellText(value: Date): string {
  if (Number.isNaN(value.getTime())) return '';
  const p = (n: number, width = 2) => String(n).padStart(width, '0');
  return `${p(value.getUTCFullYear(), 4)}-${p(value.getUTCMonth() + 1)}-${p(value.getUTCDate())}`;
}
